<template>
    <div class="report-builder">
        <!-- ── dark app header ─────────────────────────────────────── -->
        <header class="rb-topbar">
            <div class="rb-brand">
                <div class="rb-brand-icon"><v-icon size="17">mdi-chart-box-outline</v-icon></div>
                <div class="rb-brand-titles">
                    <span class="rb-brand-title">Report Builder</span>
                    <span class="rb-brand-sub">{{ project && project.title ? project.title : "AI visibility reporting" }}</span>
                </div>
            </div>

            <div class="rb-topbar-divider"></div>

            <!-- report picker: replaces the old "Saved reports" card -->
            <v-menu offset-y nudge-bottom="6" min-width="340" content-class="rb-menu">
                <template v-slot:activator="{ on, attrs }">
                    <div class="rb-report-picker" v-bind="attrs" v-on="on">
                        <span class="rb-report-picker-name">{{ draft.name || "New Report" }}</span>
                        <span class="rb-report-picker-meta">{{ enabledCount }} sections</span>
                        <v-icon size="14" class="rb-report-picker-chevron">mdi-chevron-down</v-icon>
                    </div>
                </template>
                <div class="rb-menu-inner">
                    <div class="rb-menu-head">
                        <span>Saved reports</span>
                        <v-btn text x-small color="primary" @click="newReport"><v-icon left x-small>mdi-plus</v-icon>New report</v-btn>
                    </div>
                    <div v-if="savedReports.length" class="rb-menu-list">
                        <div v-for="r in savedReports" :key="r._id" class="rb-menu-item" :class="{ 'rb-menu-item--active': draft._id === r._id }" @click="loadReport(r)">
                            <div class="rb-menu-item-main">
                                <span class="rb-menu-item-name">{{ r.name }}</span>
                                <span class="rb-menu-item-sub">{{ (r.sections || []).filter((s) => s.enabled !== false).length }} sections</span>
                            </div>
                            <v-btn icon x-small @click.stop="deleteReport(r)"><v-icon x-small>mdi-delete-outline</v-icon></v-btn>
                        </div>
                    </div>
                    <div v-else class="rb-menu-empty">
                        <v-icon size="20" color="#33b9a7">mdi-folder-outline</v-icon>
                        <span class="rb-empty-title">No saved reports yet</span>
                        <span class="rb-empty-text">Build one on the right and save it — reuse it every month with a fresh date range.</span>
                    </div>
                </div>
            </v-menu>

            <v-spacer />

            <span v-if="isDirty" class="rb-status rb-status--dirty"><v-icon size="13" color="#e5a400">mdi-circle-medium</v-icon>Unsaved changes</span>
            <span v-else-if="draft._id" class="rb-status rb-status--saved"><v-icon size="13" color="#1cedb3">mdi-check</v-icon>All changes saved</span>

            <button v-if="draft._id" class="rb-btn-ghost" :disabled="sharing" @click="getShareLink"><v-icon size="15" class="mr-1">mdi-share-variant-outline</v-icon>Share link</button>
            <button v-if="canPreview" class="rb-btn-accent" :disabled="!pdfReady || pdfLoading" @click="triggerDownloadPdf">
                <v-icon size="15" class="mr-2">mdi-file-pdf-box</v-icon>{{ pdfReady ? "Download PDF" : "Loading data …" }}
            </button>
            <button class="rb-btn-icon" :title="'Back to project'" @click="$router.push(`/ai-suite/project/${projectId}`)"><v-icon size="16">mdi-arrow-left</v-icon></button>
        </header>

        <!-- ── body ────────────────────────────────────────────────── -->
        <div class="rb-body">
            <div class="rb-sidebar">
                <!-- tabs -->
                <div class="rb-tabs">
                    <div v-for="tab in tabs" :key="tab.key" class="rb-tab" :class="{ 'rb-tab--active': activeTab === tab.key }" @click="activeTab = tab.key">
                        <v-icon size="14">{{ tab.icon }}</v-icon>
                        <span>{{ tab.label }}</span>
                        <span v-if="tab.key === 'sections' && draft.sections.length" class="rb-tab-count">{{ draft.sections.length }}</span>
                    </div>
                </div>

                <!-- shared date range — always visible, it governs every section -->
                <div class="rb-daterange">
                    <v-icon size="16" color="#1cedb3">mdi-calendar-blank-outline</v-icon>
                    <div class="rb-daterange-main">
                        <span class="rb-daterange-text">{{ rangeText }}</span>
                        <span class="rb-daterange-sub">{{ rangeDayCount ? rangeDayCount + " days · " : "" }}applies to all sections</span>
                    </div>
                    <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y nudge-bottom="6" min-width="324" max-width="324" content-class="rb-menu rb-menu--date">
                        <template v-slot:activator="{ on, attrs }">
                            <button class="rb-daterange-btn" v-bind="attrs" v-on="on">Change</button>
                        </template>
                        <div class="rb-menu-inner">
                            <div class="rb-menu-head"><span>Date range</span></div>
                            <div class="rb-segment">
                                <div
                                    v-for="preset in datePresets"
                                    :key="preset.key"
                                    class="rb-segment-item"
                                    :class="{ 'rb-segment-item--active': activePreset === preset.key }"
                                    @click="pickPreset(preset)">
                                    {{ preset.label }}
                                </div>
                            </div>
                            <div class="rb-menu-sep">
                                <span>or pick your own</span>
                            </div>
                            <v-date-picker
                                v-model="customRange"
                                range
                                no-title
                                flat
                                width="300"
                                :allowed-dates="isAllowedDate"
                                :first-day-of-week="1"
                                color="primary"
                                class="rb-datepicker"
                                @change="applyCustomRange"></v-date-picker>
                        </div>
                    </v-menu>
                </div>

                <!-- tab content -->
                <div class="rb-panel">
                    <div class="rb-panel-scroll">
                        <!-- ── SETUP ── -->
                        <template v-if="activeTab === 'setup'">
                            <div class="rb-card">
                                <div class="rb-card-head">Report</div>
                                <v-text-field v-model="draft.name" label="Report name" dense outlined hide-details class="mb-3"></v-text-field>
                                <v-textarea
                                    v-model="draft.summary"
                                    label="Executive summary"
                                    placeholder="Intro commentary for the client …"
                                    rows="4"
                                    auto-grow
                                    dense
                                    outlined
                                    hide-details></v-textarea>
                                <div class="rb-hint">Shown as an intro block at the start of the report.</div>
                            </div>
                        </template>

                        <!-- ── SECTIONS ── -->
                        <template v-else-if="activeTab === 'sections'">
                            <div class="rb-card">
                                <div class="rb-card-head rb-card-head--accent"><v-icon size="13">mdi-plus</v-icon>Add a section</div>
                                <v-select v-model="newSection.type" :items="elementItems" item-text="label" item-value="type" label="Element" dense outlined hide-details class="mb-2">
                                    <template v-slot:item="{ item }">
                                        <v-icon small class="mr-2">{{ item.icon }}</v-icon
                                        >{{ item.label }}
                                    </template>
                                </v-select>
                                <div class="rb-add-grid mb-2">
                                    <v-select v-model="newSection.platform" :items="platformItems" item-text="label" item-value="key" label="Platform" dense outlined hide-details>
                                        <template v-slot:item="{ item }">
                                            <span v-if="item.logo" class="rb-platform-logo mr-2" v-html="item.logo"></span>
                                            <v-icon v-else small class="mr-2">mdi-earth</v-icon>{{ item.label }}
                                        </template>
                                        <template v-slot:selection="{ item }"> <span v-if="item.logo" class="rb-platform-logo mr-2" v-html="item.logo"></span>{{ item.label }} </template>
                                    </v-select>
                                    <v-select v-model="newSection.tag" :items="tagItems" label="Tag" dense outlined hide-details clearable></v-select>
                                </div>
                                <button class="rb-btn-primary rb-btn-block" @click="addSection"><v-icon size="15" class="mr-2">mdi-plus</v-icon>Add to report</button>
                            </div>

                            <div class="rb-card rb-card--grow">
                                <div class="rb-card-head">
                                    <span>In this report</span>
                                    <span v-if="draft.sections.length > 1" class="rb-card-head-hint">drag to reorder</span>
                                </div>

                                <draggable v-if="draft.sections.length" :list="draft.sections" ghost-class="rb-ghost" handle=".rb-handle" class="rb-seclist" @end="reindexSections">
                                    <div v-for="section in draft.sections" :key="section.id" class="rb-sec" :class="{ 'rb-sec--off': !section.enabled }">
                                        <div class="rb-sec-row">
                                            <span class="rb-handle"><v-icon size="14">mdi-drag-vertical</v-icon></span>
                                            <div class="rb-sec-main">
                                                <span class="rb-sec-label">{{ metaFor(section.type) ? metaFor(section.type).label : section.type }}</span>
                                                <div v-if="section.enabled" class="rb-sec-badges">
                                                    <span class="rb-badge rb-badge--platform">
                                                        <span v-if="platformLogoFor(section.platform)" class="rb-platform-logo" v-html="platformLogoFor(section.platform)"></span>
                                                        {{ platformLabelFor(section.platform) }}
                                                    </span>
                                                    <span v-if="section.tag" class="rb-badge rb-badge--tag">{{ section.tag }}</span>
                                                </div>
                                                <span v-else class="rb-sec-offtext">Off — not included in the PDF</span>
                                            </div>
                                            <button
                                                v-if="section.enabled && hasOptions(section.type)"
                                                class="rb-sec-btn"
                                                :class="{ 'rb-sec-btn--active': optionsOpen(section) }"
                                                @click="toggleOptions(section)">
                                                <v-icon size="14">mdi-cog-outline</v-icon>
                                            </button>
                                            <v-switch v-model="section.enabled" inset hide-details dense class="rb-sec-switch mt-0 pt-0"></v-switch>
                                            <button class="rb-sec-btn rb-sec-btn--danger" @click="removeSection(section)"><v-icon size="14">mdi-close</v-icon></button>
                                        </div>
                                        <div v-if="section.enabled && optionsOpen(section)" class="rb-sec-opts">
                                            <v-checkbox
                                                v-for="opt in metaFor(section.type).options || []"
                                                :key="opt.key"
                                                v-model="section.options[opt.key]"
                                                :label="opt.label"
                                                dense
                                                hide-details
                                                class="mt-0 pt-0"></v-checkbox>
                                            <v-select
                                                v-if="metaFor(section.type).brandPicker"
                                                :value="brandModeFor(section)"
                                                :items="trendBrandModes"
                                                label="Brands shown"
                                                dense
                                                outlined
                                                hide-details
                                                class="mt-3"
                                                @change="(v) => setSectionBrandMode(section, v)"></v-select>
                                            <v-autocomplete
                                                v-if="metaFor(section.type).brandPicker && brandModeFor(section) === 'custom'"
                                                :value="section.options.brands || []"
                                                :items="competitorItems"
                                                label="Competitors"
                                                placeholder="Pick competitors …"
                                                multiple
                                                chips
                                                small-chips
                                                deletable-chips
                                                clearable
                                                dense
                                                outlined
                                                hide-details
                                                class="mt-3"
                                                @change="(v) => setSectionBrands(section, v)"></v-autocomplete>
                                            <div v-if="metaFor(section.type).brandPicker" class="rb-hint">{{ brandModeHint(section) }}</div>
                                        </div>
                                    </div>
                                </draggable>

                                <div v-else class="rb-empty rb-empty--sections">
                                    <div class="rb-empty-icon"><v-icon size="22" color="primary">mdi-view-dashboard-outline</v-icon></div>
                                    <span class="rb-empty-title">No sections yet</span>
                                    <span class="rb-empty-text">Pick an element above to add your first block. Most reports start with KPI cards and visibility over time.</span>
                                    <div class="rb-empty-chips">
                                        <span v-for="q in quickAdds" :key="q.type" class="rb-empty-chip" @click="quickAdd(q.type)">+ {{ q.label }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- ── BRAND ── -->
                        <template v-else>
                            <div class="rb-card">
                                <div class="rb-card-head">Logo</div>
                                <div class="rb-logo-row">
                                    <img v-if="draft.branding && draft.branding.logoUrl" :src="draft.branding.logoUrl" class="rb-logo-preview" alt="Logo" />
                                    <v-btn small outlined color="primary" @click="$refs.logoInput.click()">
                                        <v-icon left small>mdi-image-plus</v-icon>{{ draft.branding && draft.branding.logoUrl ? "Change logo" : "Upload logo" }}
                                    </v-btn>
                                    <v-btn v-if="draft.branding && draft.branding.logoUrl" icon small @click="removeLogo"><v-icon small>mdi-close</v-icon></v-btn>
                                    <input ref="logoInput" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style="display: none" @change="onLogoSelected" />
                                </div>
                                <div class="rb-hint">Shown on every slide and in the PDF. With a logo set, the QUARO footer is removed (white-label).</div>
                            </div>

                            <div class="rb-card">
                                <div class="rb-card-head">Accent color</div>
                                <div class="rb-color-row">
                                    <v-menu :close-on-content-click="false" offset-y>
                                        <template v-slot:activator="{ on, attrs }">
                                            <div class="rb-color-swatch" :style="{ background: accentColor }" v-bind="attrs" v-on="on"></div>
                                        </template>
                                        <v-color-picker v-model="accentColor" mode="hexa" hide-mode-switch dot-size="18" show-swatches></v-color-picker>
                                    </v-menu>
                                    <span class="rb-color-value">{{ accentColor }}</span>
                                    <v-btn v-if="draft.branding && draft.branding.primaryColor" text x-small @click="resetAccent">Reset</v-btn>
                                </div>
                                <div class="rb-hint">Colors bars, dividers and highlights in the report.</div>
                            </div>

                            <div v-if="shareUrl" class="rb-card">
                                <div class="rb-card-head">Share link</div>
                                <div class="rb-sharebox">
                                    <span class="rb-sharebox-url">{{ shareUrl }}</span>
                                    <button class="rb-sharebox-copy" @click="copyShareUrl">Copy</button>
                                </div>
                            </div>
                        </template>
                    </div>

                    <!-- pinned action bar -->
                    <div class="rb-actions">
                        <button class="rb-btn-primary rb-btn-block rb-btn-lg" :disabled="saving" @click="saveReport">
                            <v-icon size="16" class="mr-2">mdi-content-save-outline</v-icon>{{ draft._id ? "Update report" : "Save report" }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- ── preview column (renderer unchanged) ──────────────── -->
            <div class="rb-preview-col">
                <div class="rb-preview-bar">
                    <span class="rb-preview-pill"><span class="rb-preview-dot"></span>Live preview</span>
                    <v-spacer />
                    <span v-if="canPreview" class="rb-preview-meta">{{ enabledCount }} of {{ draft.sections.length }} sections visible</span>
                </div>
                <div class="rb-preview-paper">
                    <report-renderer
                        v-if="canPreview"
                        ref="renderer"
                        :report="draft"
                        :project="project"
                        :company-id="companyId"
                        :show-actions="false"
                        @ready-change="pdfReady = $event"
                        @loading-change="pdfLoading = $event" />
                    <div v-else class="rb-empty rb-empty--preview">
                        <div class="rb-empty-icon rb-empty-icon--plain"><v-icon size="22" color="#8fbdb8">mdi-eye-outline</v-icon></div>
                        <span class="rb-empty-title">Nothing to preview yet</span>
                        <span class="rb-empty-text">Add at least one enabled section and pick a date range — the preview updates as you edit.</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import draggable from "vuedraggable"
    import ReportRenderer from "../../components/reports/ReportRenderer.vue"
    import { resolveApiRoot, defaultSections, normalizeSections, makeSection, SECTION_REGISTRY, sectionMeta, fetchBrandsAll, TREND_BRAND_MODES, trendBrandMode } from "../../components/reports/reportApi"
    import { getPlatformMeta } from "../../utils/platformMeta"

    const PLATFORM_DATE_FIELD = {
        chatgpt: "processed_dates_ai_tracking_chatgpt",
        gemini: "processed_dates_ai_tracking_gemini",
        google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
        perplexity: "processed_dates_ai_tracking_perplexity",
        grok: "processed_dates_ai_tracking_grok",
        microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot"
    }

    const DEFAULT_ACCENT = "#009f89"

    function emptyDraft(projectId) {
        return {
            _id: null,
            project_id: projectId,
            name: "New Report",
            template: "brand_visibility",
            summary: "",
            language: "en",
            sections: defaultSections(),
            // filters is now report-level only: the shared date range. Platform/tag live per section.
            filters: { selectedDates: [] },
            branding: { logoUrl: null, primaryColor: null }
        }
    }

    function normalizeFilters(filters) {
        const f = filters || {}
        if (!Array.isArray(f.selectedDates)) f.selectedDates = []
        return f
    }

    export default {
        name: "ReportBuilder",
        components: { draggable, ReportRenderer },
        data() {
            return {
                project: null,
                savedReports: [],
                draft: emptyDraft(this.$route.params.id),
                availableTags: [],
                projectBrands: [],
                saving: false,
                sharing: false,
                shareUrl: "",
                // section id → open/closed; ids missing here use the per-type default (see optionsOpen)
                expandedOptions: {},
                activePreset: "30",
                activeTab: "sections",
                dateMenu: false,
                customRange: [],
                savedSnapshot: "",
                newSection: { type: "kpi_cards", platform: "all", tag: null },
                pdfReady: false,
                pdfLoading: false,
                tabs: [
                    { key: "setup", label: "Setup", icon: "mdi-tune-variant" },
                    { key: "sections", label: "Sections", icon: "mdi-view-dashboard-outline" },
                    { key: "brand", label: "Brand", icon: "mdi-palette-outline" }
                ],
                datePresets: [
                    { key: "7", label: "7 days", days: 7 },
                    { key: "14", label: "14 days", days: 14 },
                    { key: "30", label: "30 days", days: 30 },
                    { key: "all", label: "All", days: null }
                ]
            }
        },
        computed: {
            projectId() {
                return this.$route.params.id
            },
            companyId() {
                return localStorage.company_id
            },
            enabledCount() {
                return (this.draft.sections || []).filter((s) => s.enabled).length
            },
            // element choices for the add-section form
            elementItems() {
                return SECTION_REGISTRY.map((s) => ({ type: s.type, label: s.label, icon: s.icon }))
            },
            // first three registry entries double as one-click starters in the empty state
            quickAdds() {
                return SECTION_REGISTRY.slice(0, 3).map((s) => ({ type: s.type, label: s.label }))
            },
            // "All Platforms" + each tracked platform, for the section platform picker
            platformItems() {
                const tracked = Array.isArray(this.project?.platforms) ? this.project.platforms.map((p) => (p || "").toLowerCase()) : []
                // Google AIO isn't stored in the platforms array — it's tracked via the
                // separate project.google flag, so add it explicitly when enabled.
                if (this.project && this.project.google && !tracked.includes("google")) tracked.push("google")
                const items = [{ key: "all", label: "All Platforms", logo: null }]
                // Grok wird nicht mehr angeboten: bestehende Grok-Abschnitte werden weiter angezeigt,
                // für neue Abschnitte ist die Plattform aber nicht mehr wählbar.
                tracked
                    .filter((k) => k !== "openai" && k !== "grok")
                    .forEach((k) => {
                        const meta = getPlatformMeta(k)
                        items.push({ key: k, label: meta.label || k, logo: meta.logo })
                    })
                return items
            },
            // competitor choices for the trend chart picker — own brand excluded, it's always shown
            competitorItems() {
                const own = (this.project?.brand?.brand_name || "").toLowerCase()
                const names = this.projectBrands.map((b) => b?.canonicalName).filter((n) => n && n.toLowerCase() !== own)
                return [...new Set(names)].sort((a, b) => a.localeCompare(b))
            },
            trendBrandModes() {
                return TREND_BRAND_MODES.map((m) => ({ text: m.label, value: m.value }))
            },
            favoriteCount() {
                return Array.isArray(this.project?.ai_brand_favorites) ? this.project.ai_brand_favorites.length : 0
            },
            tagItems() {
                return [{ text: "— no tag —", value: null }, ...this.availableTags.map((t) => ({ text: t, value: t }))]
            },
            // date range spans every tracked platform's processed days
            availableDates() {
                if (!this.project) return []
                const all = []
                Object.keys(PLATFORM_DATE_FIELD).forEach((k) => {
                    const arr = this.project[PLATFORM_DATE_FIELD[k]]
                    if (Array.isArray(arr)) all.push(...arr)
                })
                return Array.from(
                    new Set(
                        all
                            .map((d) => {
                                const dt = new Date(d)
                                return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                            })
                            .filter(Boolean)
                    )
                ).sort()
            },
            rangeText() {
                const dates = this.draft.filters.selectedDates || []
                if (!dates.length) return "No data available"
                return dates.length > 1 ? `${dates[0]} – ${dates[dates.length - 1]}` : dates[0]
            },
            rangeDayCount() {
                const dates = this.draft.filters.selectedDates || []
                if (dates.length < 2) return 0
                const start = new Date(`${dates[0]}T00:00:00Z`)
                const end = new Date(`${dates[dates.length - 1]}T00:00:00Z`)
                const diff = Math.round((end - start) / 86400000)
                return diff > 0 ? diff + 1 : 0
            },
            canPreview() {
                return this.draft.name && (this.draft.sections || []).some((s) => s.enabled) && (this.draft.filters.selectedDates || []).length > 0
            },
            // two-way accent color: reads branding.primaryColor, falls back to the QUARO green
            accentColor: {
                get() {
                    return (this.draft.branding && this.draft.branding.primaryColor) || DEFAULT_ACCENT
                },
                set(val) {
                    if (!this.draft.branding) this.$set(this.draft, "branding", { logoUrl: null, primaryColor: null })
                    // v-color-picker hexa returns #RRGGBBAA — keep the plain hex
                    this.$set(this.draft.branding, "primaryColor", (val || "").slice(0, 7))
                }
            },
            isDirty() {
                return this.snapshot(this.draft) !== this.savedSnapshot
            }
        },
        methods: {
            resolveApiRoot() {
                return resolveApiRoot(this.companyId)
            },
            triggerDownloadPdf() {
                if (this.$refs.renderer && typeof this.$refs.renderer.downloadPdf === "function") {
                    this.$refs.renderer.downloadPdf()
                }
            },
            metaFor(type) {
                return sectionMeta(type)
            },
            platformLabelFor(key) {
                return !key || key === "all" ? "All Platforms" : getPlatformMeta(key).label || key
            },
            platformLogoFor(key) {
                return !key || key === "all" ? null : getPlatformMeta(key).logo || null
            },
            hasOptions(type) {
                const meta = sectionMeta(type)
                return !!meta && ((meta.options || []).length > 0 || !!meta.brandPicker)
            },
            brandModeFor(section) {
                return trendBrandMode(section.options)
            },
            setSectionBrandMode(section, mode) {
                this.$set(section.options, "brand_mode", mode)
            },
            brandModeHint(section) {
                const mode = this.brandModeFor(section)
                if (mode === "favorites") {
                    return this.favoriteCount
                        ? `${this.favoriteCount} favorites from "Manage Brands". Your own brand is always shown.`
                        : 'No favorites set for this project — falls back to top 12. Set favorites in "Manage Brands".'
                }
                if (mode === "custom" && !((section.options || {}).brands || []).length) return "Nothing picked yet — falls back to top 12. Your own brand is always shown."
                return "Your own brand is always shown."
            },
            setSectionBrands(section, value) {
                const brands = Array.isArray(value) ? value : []
                if (brands.length) this.$set(section.options, "brands", brands)
                else this.$delete(section.options, "brands")
            },
            async fetchProjectBrands() {
                try {
                    this.projectBrands = await fetchBrandsAll(this.companyId, this.projectId)
                } catch (e) {
                    this.projectBrands = []
                }
            },
            // sections with a brand picker start expanded so the competitor choice is visible
            optionsOpen(section) {
                if (!this.hasOptions(section.type)) return false
                if (section.id in this.expandedOptions) return this.expandedOptions[section.id]
                return !!sectionMeta(section.type).brandPicker
            },
            toggleOptions(section) {
                this.$set(this.expandedOptions, section.id, !this.optionsOpen(section))
            },
            reindexSections() {
                this.draft.sections.forEach((s, i) => (s.order = i))
            },
            addSection() {
                const s = makeSection(this.newSection.type, this.newSection.platform, this.newSection.tag)
                s.order = this.draft.sections.length
                this.draft.sections.push(s)
                this.newSection.tag = null
            },
            quickAdd(type) {
                const s = makeSection(type, "all", null)
                s.order = this.draft.sections.length
                this.draft.sections.push(s)
            },
            removeSection(section) {
                const i = this.draft.sections.indexOf(section)
                if (i > -1) this.draft.sections.splice(i, 1)
                this.reindexSections()
            },
            applyPreset(preset) {
                const dates = this.availableDates
                if (!dates.length) {
                    this.draft.filters.selectedDates = []
                    return
                }
                const to = dates[dates.length - 1]
                if (!preset.days) {
                    this.draft.filters.selectedDates = dates.length > 1 ? [dates[0], to] : [to]
                    return
                }
                const cutoff = new Date(`${to}T00:00:00.000Z`)
                cutoff.setUTCDate(cutoff.getUTCDate() - (preset.days - 1))
                const cutoffIso = cutoff.toISOString().slice(0, 10)
                const inRange = dates.filter((d) => d >= cutoffIso)
                this.draft.filters.selectedDates = inRange.length > 1 ? [inRange[0], to] : [to]
            },
            // preset click from inside the date menu
            pickPreset(preset) {
                this.activePreset = preset.key
                this.applyPreset(preset)
                this.dateMenu = false
            },
            applyActivePreset() {
                if (this.activePreset === "custom") return
                const preset = this.datePresets.find((p) => p.key === this.activePreset) || this.datePresets[2]
                this.applyPreset(preset)
            },
            // v-date-picker only allows days the project actually has data for
            isAllowedDate(iso) {
                return this.availableDates.includes(iso)
            },
            applyCustomRange(range) {
                const picked = (Array.isArray(range) ? range : []).filter(Boolean).sort()
                if (!picked.length) return
                this.activePreset = "custom"
                this.draft.filters.selectedDates = picked.length > 1 ? [picked[0], picked[picked.length - 1]] : [picked[0]]
                this.dateMenu = false
            },
            resetAccent() {
                if (this.draft.branding) this.$set(this.draft.branding, "primaryColor", null)
            },
            // stable JSON of the meaningful report fields, for unsaved-change detection
            snapshot(d) {
                return JSON.stringify({ name: d.name, summary: d.summary, sections: d.sections, filters: d.filters, branding: d.branding })
            },
            markSaved() {
                this.savedSnapshot = this.snapshot(this.draft)
            },
            onLogoSelected(ev) {
                const file = ev.target.files && ev.target.files[0]
                ev.target.value = ""
                if (!file) return
                if (file.size > 512 * 1024) {
                    alert("Logo file is too large (max 500 KB). Please use a smaller image.")
                    return
                }
                // stored as a data URL: no CORS issues in the PDF capture and the
                // public share view works without access to external hosts
                const reader = new FileReader()
                reader.onload = () => {
                    if (!this.draft.branding) this.$set(this.draft, "branding", { logoUrl: null, primaryColor: null })
                    this.$set(this.draft.branding, "logoUrl", reader.result)
                }
                reader.readAsDataURL(file)
            },
            removeLogo() {
                if (this.draft.branding) this.$set(this.draft.branding, "logoUrl", null)
            },
            newReport() {
                this.draft = emptyDraft(this.projectId)
                this.shareUrl = ""
                this.activePreset = "30"
                this.applyActivePreset()
                this.$nextTick(this.markSaved)
            },
            loadReport(r) {
                const parsed = JSON.parse(JSON.stringify(r))
                parsed.sections = normalizeSections(parsed.sections)
                parsed.filters = normalizeFilters(parsed.filters)
                if (!parsed.branding) parsed.branding = { logoUrl: null, primaryColor: null }
                if (typeof parsed.summary !== "string") parsed.summary = ""
                if (!parsed.language) parsed.language = "en"
                this.draft = parsed
                this.activePreset = "custom"
                this.shareUrl = r.share && r.share.enabled ? this.buildShareUrl(r.share.token) : ""
                this.fetchTags()
                this.$nextTick(this.markSaved)
            },
            async fetchProject() {
                try {
                    const res = await this.$helpers.axiosPost(this.resolveApiRoot() + "/project/one", {
                        company_id: this.companyId,
                        project_id: this.projectId
                    })
                    this.project = res.data
                    if (!this.draft.filters.selectedDates.length) this.applyActivePreset()
                } catch (e) {
                    this.project = null
                }
            },
            async fetchTags() {
                try {
                    const res = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/tags/list", {
                        company_id: this.companyId,
                        project_id: this.projectId,
                        platform: null
                    })
                    this.availableTags = Array.isArray(res?.data?.tags) ? res.data.tags.map((t) => (typeof t === "string" ? t : t.tag || t.name)).filter(Boolean) : []
                } catch (e) {
                    this.availableTags = []
                }
            },
            async fetchReports() {
                try {
                    const res = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/reports/list", {
                        company_id: this.companyId,
                        project_id: this.projectId
                    })
                    this.savedReports = Array.isArray(res?.data?.reports) ? res.data.reports : []
                } catch (e) {
                    this.savedReports = []
                }
            },
            // suggest a meaningful name instead of leaving "New Report"
            suggestedName() {
                const brand = this.project?.brand?.brand_name || this.project?.title || "Report"
                const dates = this.draft.filters.selectedDates || []
                const range = dates.length ? ` · ${dates[0]}${dates.length > 1 ? "–" + dates[dates.length - 1] : ""}` : ""
                return `${brand}${range}`
            },
            async saveReport() {
                if (!this.draft.name || this.draft.name.trim() === "" || this.draft.name === "New Report") {
                    this.draft.name = this.suggestedName()
                }
                this.saving = true
                try {
                    const res = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/reports/save", {
                        company_id: this.companyId,
                        project_id: this.projectId,
                        report: this.draft
                    })
                    const saved = res.data.report
                    // merge only server-generated fields into the existing draft; replacing the
                    // whole object would retrigger the renderer's filters/sections watchers and
                    // needlessly refetch every section right after a save
                    this.$set(this.draft, "_id", saved._id)
                    this.$set(this.draft, "share", saved.share)
                    this.$set(this.draft, "createdAt", saved.createdAt)
                    this.$set(this.draft, "updatedAt", saved.updatedAt)
                    this.markSaved()
                    await this.fetchReports()
                } finally {
                    this.saving = false
                }
            },
            async deleteReport(r) {
                await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/reports/delete", { company_id: this.companyId, _id: r._id })
                if (this.draft._id === r._id) this.newReport()
                await this.fetchReports()
            },
            buildShareUrl(token) {
                return `${window.location.origin}/reports/share/${encodeURIComponent(this.companyId)}/${this.projectId}/${token}`
            },
            async getShareLink() {
                this.sharing = true
                try {
                    if (!this.draft._id) await this.saveReport()
                    const res = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/reports/share/enable", {
                        company_id: this.companyId,
                        _id: this.draft._id
                    })
                    this.shareUrl = this.buildShareUrl(res.data.share.token)
                    this.activeTab = "brand"
                } finally {
                    this.sharing = false
                }
            },
            copyShareUrl() {
                navigator.clipboard.writeText(this.shareUrl)
            }
        },
        async mounted() {
            await this.fetchProject()
            await Promise.all([this.fetchTags(), this.fetchReports(), this.fetchProjectBrands()])
            this.$nextTick(this.markSaved)
        }
    }
</script>

<style scoped>
    /* ─────────────────────────────────────────────────────────────
       QUARO tokens (local to this view)
       ───────────────────────────────────────────────────────────── */
    .report-builder {
        --q-primary: #009f89;
        --q-primary-dark: #007a6b;
        --q-accent: #1cedb3;
        --q-dark: #0a1f1c;
        --q-fg1: #0d2622;
        --q-fg2: #2d5a54;
        --q-fg3: #5a8c84;
        --q-fg4: #8fbdb8;
        --q-border: #d4ede9;
        --q-border-light: #eaf5f3;
        --q-surface: #f2faf8;
        --q-subtle: #e0f5f2;

        display: flex;
        flex-direction: column;
        /* sits inside v-main, which starts 64px below the viewport top (global app bar).
           Subtract that so the component fits exactly and the pinned save button stays
           inside the viewport instead of being pushed off the bottom. */
        height: calc(100vh - 64px);
        min-height: 0;
        overflow: hidden;
        background: var(--q-surface);
        font-family: "Plus Jakarta Sans", system-ui, -apple-system, sans-serif;
    }

    /* ── dark app header ─────────────────────────────────────────── */
    .rb-topbar {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 14px;
        height: 68px;
        padding: 0 22px;
        background: var(--q-dark);
    }
    .rb-brand {
        display: flex;
        align-items: center;
        gap: 11px;
    }
    .rb-brand-icon {
        width: 30px;
        height: 30px;
        border-radius: 8px;
        background: rgba(28, 237, 179, 0.14);
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .rb-brand-icon .v-icon {
        color: var(--q-accent) !important;
    }
    .rb-brand-titles {
        display: flex;
        flex-direction: column;
        line-height: 1.25;
    }
    .rb-brand-title {
        font-size: 0.94rem;
        font-weight: 700;
        color: #fff;
        letter-spacing: -0.01em;
    }
    .rb-brand-sub {
        font-size: 0.69rem;
        color: #7fb3aa;
    }
    .rb-topbar-divider {
        width: 1px;
        height: 30px;
        background: rgba(255, 255, 255, 0.12);
    }

    .rb-report-picker {
        display: flex;
        align-items: center;
        gap: 9px;
        min-width: 280px;
        max-width: 380px;
        height: 38px;
        padding: 0 12px;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.07);
        border: 1px solid rgba(255, 255, 255, 0.1);
        cursor: pointer;
        transition: background 150ms ease;
    }
    .rb-report-picker:hover {
        background: rgba(255, 255, 255, 0.13);
    }
    .rb-report-picker-name {
        flex: 1 1 auto;
        min-width: 0;
        font-size: 0.81rem;
        font-weight: 600;
        color: #fff;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .rb-report-picker-meta {
        flex: 0 0 auto;
        font-size: 0.69rem;
        color: #7fb3aa;
    }
    .rb-report-picker-chevron {
        color: #7fb3aa !important;
    }

    .rb-status {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.74rem;
        font-weight: 600;
        white-space: nowrap;
    }
    .rb-status--dirty {
        color: #f0c14b;
    }
    .rb-status--saved {
        color: var(--q-accent);
    }

    /* header buttons */
    .rb-btn-ghost,
    .rb-btn-accent,
    .rb-btn-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 38px;
        border-radius: 10px;
        font-size: 0.81rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 150ms ease;
        white-space: nowrap;
    }
    .rb-btn-ghost {
        padding: 0 14px;
        border: 1px solid rgba(255, 255, 255, 0.16);
        background: transparent;
        color: #d7ece8;
    }
    .rb-btn-ghost:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
    }
    .rb-btn-accent {
        padding: 0 16px;
        border: none;
        background: linear-gradient(135deg, #009f89 0%, #1cedb3 100%);
        color: #04231f;
        font-weight: 700;
    }
    .rb-btn-accent:hover {
        filter: brightness(1.06);
        transform: translateY(-1px);
    }
    .rb-btn-accent:disabled,
    .rb-btn-ghost:disabled {
        opacity: 0.45;
        cursor: default;
        transform: none;
        filter: none;
    }
    .rb-btn-icon {
        width: 38px;
        border: none;
        background: rgba(255, 255, 255, 0.07);
        color: #d7ece8;
    }
    .rb-btn-icon:hover {
        background: rgba(255, 255, 255, 0.14);
    }
    .rb-btn-ghost .v-icon,
    .rb-btn-icon .v-icon {
        color: inherit !important;
    }
    .rb-btn-accent .v-icon {
        color: #04231f !important;
    }

    /* ── body ────────────────────────────────────────────────────── */
    .rb-body {
        flex: 1 1 auto;
        box-sizing: border-box;
        min-height: 0;
        display: flex;
        gap: 22px;
        padding: 22px;
        overflow: hidden;
    }
    .rb-sidebar {
        flex: 0 0 358px;
        min-width: 320px;
        display: grid;
        grid-template-rows: auto auto minmax(0, 1fr);
        gap: 14px;
        min-height: 0;
        overflow: hidden;
    }

    /* tabs */
    .rb-tabs {
        flex: 0 0 auto;
        display: flex;
        gap: 4px;
        padding: 4px;
        background: #e4f1ee;
        border-radius: 12px;
    }
    .rb-tab {
        flex: 1 1 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 36px;
        border-radius: 9px;
        font-size: 0.81rem;
        font-weight: 600;
        color: var(--q-fg2);
        cursor: pointer;
        transition: all 150ms ease;
    }
    .rb-tab .v-icon {
        color: var(--q-fg3) !important;
    }
    .rb-tab:hover {
        color: var(--q-fg1);
    }
    .rb-tab--active {
        background: #fff;
        color: var(--q-fg1);
        font-weight: 700;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }
    .rb-tab--active .v-icon {
        color: var(--q-primary) !important;
    }
    .rb-tab-count {
        min-width: 20px;
        height: 18px;
        padding: 0 6px;
        border-radius: 999px;
        background: var(--q-subtle);
        color: var(--q-primary-dark);
        font-size: 0.68rem;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    /* shared date range strip */
    .rb-daterange {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 11px 14px;
        border-radius: 12px;
        background: var(--q-fg1);
        color: #fff;
    }
    .rb-daterange-main {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        line-height: 1.3;
    }
    .rb-daterange-text {
        font-size: 0.81rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .rb-daterange-sub {
        font-size: 0.69rem;
        color: #7fb3aa;
    }
    .rb-daterange-btn {
        flex: 0 0 auto;
        height: 28px;
        padding: 0 11px;
        border: none;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        font-size: 0.74rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 150ms ease;
    }
    .rb-daterange-btn:hover {
        background: rgba(255, 255, 255, 0.2);
    }

    /* tab content panel + pinned actions */
    .rb-panel {
        flex: 1 1 auto;
        min-height: 0;
        display: grid;
        grid-template-rows: minmax(0, 1fr) auto;
        gap: 14px;
        overflow: hidden;
    }
    .rb-panel-scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding-right: 2px;
        padding-bottom: 8px;
    }
    .rb-card {
        flex: 0 0 auto;
        padding: 14px;
        border-radius: 12px;
        background: #fff;
        border: 1px solid var(--q-border);
        box-shadow: 0 1px 4px rgba(0, 159, 137, 0.08);
    }
    .rb-card--grow {
        flex: 1 1 auto;
        min-height: 160px;
        display: flex;
        flex-direction: column;
    }
    .rb-card-head {
        display: flex;
        align-items: center;
        gap: 7px;
        margin-bottom: 12px;
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--q-fg3);
    }
    .rb-card-head--accent {
        color: var(--q-primary);
    }
    .rb-card-head--accent .v-icon {
        color: var(--q-primary) !important;
    }
    .rb-card-head-hint {
        margin-left: auto;
        font-size: 0.68rem;
        font-weight: 500;
        letter-spacing: 0;
        text-transform: none;
        color: var(--q-fg4);
    }
    .rb-hint {
        margin-top: 6px;
        font-size: 0.7rem;
        line-height: 1.4;
        color: var(--q-fg3);
    }
    .rb-add-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }

    /* buttons in the panel */
    .rb-btn-primary {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        padding: 0 16px;
        border: none;
        border-radius: 10px;
        background: var(--q-primary);
        color: #fff;
        font-size: 0.81rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 150ms ease;
    }
    .rb-btn-primary:hover {
        background: var(--q-primary-dark);
    }
    .rb-btn-primary:disabled {
        opacity: 0.5;
        cursor: default;
    }
    .rb-btn-primary .v-icon {
        color: #fff !important;
    }
    .rb-btn-block {
        width: 100%;
    }
    .rb-btn-lg {
        height: 44px;
        border-radius: 999px;
        font-size: 0.88rem;
        box-shadow: 0 1px 4px rgba(0, 159, 137, 0.3);
    }
    .rb-btn-lg:hover {
        transform: translateY(-1px);
    }
    .rb-actions {
        flex: 0 0 auto;
        padding-top: 8px;
        background: var(--q-surface);
        border-top: 1px solid var(--q-border-light);
    }

    /* section list */
    .rb-seclist {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 7px;
        padding-right: 2px;
    }
    .rb-sec {
        flex: 0 0 auto;
        border-radius: 10px;
        background: #f7fdfc;
        border: 1px solid var(--q-subtle);
        transition: border-color 150ms ease;
    }
    .rb-sec:hover {
        border-color: #9ed3cb;
    }
    .rb-sec--off {
        background: #f8faf9;
        border-style: dashed;
        border-color: #dfe9e7;
    }
    .rb-sec-row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px;
    }
    .rb-handle {
        flex: 0 0 auto;
        display: inline-flex;
        cursor: grab;
    }
    .rb-handle .v-icon {
        color: #b9d9d4 !important;
    }
    .rb-sec-main {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .rb-sec-label {
        font-size: 0.81rem;
        font-weight: 600;
        color: var(--q-fg1);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .rb-sec--off .rb-sec-label {
        color: var(--q-fg4);
    }
    .rb-sec-offtext {
        font-size: 0.69rem;
        color: var(--q-fg4);
    }
    .rb-sec-badges {
        display: flex;
        gap: 5px;
        min-width: 0;
    }
    .rb-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 19px;
        max-width: 150px;
        padding: 0 7px;
        border-radius: 5px;
        font-size: 0.63rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .rb-badge--platform {
        background: var(--q-subtle);
        color: var(--q-primary-dark);
    }
    .rb-badge--tag {
        background: #e6f3ff;
        color: #0067b3;
    }
    .rb-sec-btn {
        flex: 0 0 auto;
        width: 26px;
        height: 26px;
        border: none;
        border-radius: 7px;
        background: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 150ms ease;
    }
    .rb-sec-btn .v-icon {
        color: var(--q-fg4) !important;
    }
    .rb-sec-btn:hover {
        background: var(--q-subtle);
    }
    .rb-sec-btn:hover .v-icon {
        color: var(--q-primary) !important;
    }
    .rb-sec-btn--active {
        background: var(--q-subtle);
    }
    .rb-sec-btn--active .v-icon {
        color: var(--q-primary) !important;
    }
    .rb-sec-btn--danger:hover {
        background: #fdeaea;
    }
    .rb-sec-btn--danger:hover .v-icon {
        color: #d94040 !important;
    }
    .rb-sec-switch {
        flex: 0 0 auto;
    }
    .rb-sec-opts {
        padding: 0 12px 10px 32px;
        border-top: 1px solid var(--q-border-light);
        padding-top: 10px;
    }
    .rb-ghost {
        opacity: 0.4;
    }
    .rb-platform-logo,
    .rb-platform-logo >>> svg {
        display: inline-flex;
        width: 13px;
        height: 13px;
        vertical-align: middle;
        border-radius: 3px;
    }

    /* branding */
    .rb-logo-row {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .rb-logo-preview {
        height: 34px;
        max-width: 120px;
        object-fit: contain;
        border: 1px solid var(--q-border);
        border-radius: 6px;
        padding: 2px 6px;
        background: #fff;
    }
    .rb-color-row {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .rb-color-swatch {
        width: 30px;
        height: 30px;
        border-radius: 7px;
        border: 1px solid var(--q-border);
        cursor: pointer;
    }
    .rb-color-value {
        font-size: 0.74rem;
        font-weight: 700;
        color: var(--q-fg2);
        text-transform: uppercase;
    }
    .rb-sharebox {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 10px;
        border: 1px solid var(--q-border);
    }
    .rb-sharebox-url {
        flex: 1 1 auto;
        min-width: 0;
        font-size: 0.72rem;
        color: var(--q-fg2);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .rb-sharebox-copy {
        flex: 0 0 auto;
        height: 26px;
        padding: 0 10px;
        border: 1px solid var(--q-border);
        border-radius: 7px;
        background: var(--q-surface);
        color: var(--q-primary);
        font-size: 0.72rem;
        font-weight: 700;
        cursor: pointer;
    }
    .rb-sharebox-copy:hover {
        background: var(--q-subtle);
    }

    /* dropdown menus (report picker, date range) */
    .rb-menu {
        border-radius: 12px !important;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
    }
    .rb-menu-inner {
        padding: 12px;
        background: #fff;
    }
    .rb-menu-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--q-fg3);
    }
    .rb-menu-list {
        max-height: 260px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .rb-menu-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        border-radius: 9px;
        cursor: pointer;
        transition: background 150ms ease;
    }
    .rb-menu-item:hover {
        background: var(--q-surface);
    }
    .rb-menu-item--active {
        background: var(--q-subtle);
        box-shadow: inset 3px 0 0 var(--q-primary);
    }
    .rb-menu-item-main {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        line-height: 1.3;
    }
    .rb-menu-item-name {
        font-size: 0.81rem;
        font-weight: 600;
        color: var(--q-fg1);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .rb-menu-item-sub {
        font-size: 0.69rem;
        color: var(--q-fg3);
    }
    .rb-menu-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 8px;
        padding: 16px 8px 8px;
    }
    .rb-menu--date {
        width: 324px;
    }
    /* "or pick your own" divider between presets and the calendar */
    .rb-menu-sep {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 12px 0 4px;
        font-size: 0.68rem;
        font-weight: 600;
        color: var(--q-fg4);
    }
    .rb-menu-sep::before,
    .rb-menu-sep::after {
        content: "";
        flex: 1 1 auto;
        height: 1px;
        background: var(--q-border-light);
    }
    .rb-datepicker {
        margin: 0 -2px;
        box-shadow: none !important;
    }
    .rb-datepicker >>> .v-date-picker-table {
        height: auto;
        padding: 0 4px;
    }
    .rb-datepicker >>> .v-date-picker-table .v-btn {
        font-size: 0.78rem;
        font-weight: 500;
    }
    .rb-datepicker >>> .v-date-picker-header {
        padding: 0 4px 4px;
    }
    .rb-datepicker >>> .v-date-picker-header .v-date-picker-header__value button {
        font-size: 0.81rem;
        font-weight: 700;
        color: var(--q-fg1);
    }
    .rb-datepicker >>> .v-date-picker-table th {
        font-size: 0.66rem;
        font-weight: 700;
        color: var(--q-fg4);
    }

    /* segmented preset control inside the date menu */
    .rb-segment {
        display: flex;
        gap: 4px;
        padding: 3px;
        border-radius: 999px;
        background: var(--q-surface);
        border: 1px solid var(--q-border-light);
    }
    .rb-segment-item {
        flex: 1 1 0;
        min-width: 0;
        white-space: nowrap;
        text-align: center;
        height: 30px;
        line-height: 30px;
        border-radius: 999px;
        font-size: 0.72rem;
        font-weight: 600;
        color: var(--q-fg3);
        cursor: pointer;
        transition: all 150ms ease;
    }
    .rb-segment-item:hover {
        color: var(--q-fg1);
    }
    .rb-segment-item--active {
        background: var(--q-primary);
        /* color: #fff; */
        font-weight: 700;
        box-shadow: 0 1px 3px rgba(0, 159, 137, 0.35);
    }

    /* empty states */
    .rb-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 10px;
    }
    .rb-empty--sections {
        flex: 1 1 auto;
        justify-content: center;
        padding: 20px 14px;
        border-radius: 12px;
        border: 1px dashed #9ed3cb;
        background: #f7fdfc;
    }
    .rb-empty--preview {
        height: 100%;
        justify-content: center;
        padding: 24px;
    }
    .rb-empty-icon {
        width: 46px;
        height: 46px;
        border-radius: 12px;
        background: var(--q-subtle);
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .rb-empty-icon--plain {
        background: #fff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }
    .rb-empty-title {
        font-size: 0.94rem;
        font-weight: 700;
        color: var(--q-fg1);
    }
    .rb-empty-text {
        max-width: 340px;
        font-size: 0.81rem;
        line-height: 1.5;
        color: var(--q-fg2);
    }
    .rb-empty-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        justify-content: center;
    }
    .rb-empty-chip {
        height: 28px;
        padding: 0 11px;
        border-radius: 999px;
        background: #fff;
        border: 1px solid var(--q-border);
        font-size: 0.74rem;
        font-weight: 600;
        color: var(--q-fg1);
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        transition: all 150ms ease;
    }
    .rb-empty-chip:hover {
        border-color: var(--q-primary);
        color: var(--q-primary);
    }

    /* ── preview column (renderer untouched) ─────────────────────── */
    .rb-preview-col {
        flex: 1 1 0%;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-height: 0;
    }
    .rb-preview-bar {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .rb-preview-pill {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        height: 30px;
        padding: 0 12px;
        border-radius: 999px;
        background: #fff;
        border: 1px solid var(--q-border);
        font-size: 0.74rem;
        font-weight: 700;
        color: var(--q-fg1);
    }
    .rb-preview-dot {
        width: 7px;
        height: 7px;
        border-radius: 999px;
        background: var(--q-accent);
    }
    .rb-preview-meta {
        font-size: 0.74rem;
        color: var(--q-fg3);
    }
    .rb-preview-paper {
        flex: 1 1 auto;
        min-height: 0;
        border-radius: 14px;
        border: 1px solid var(--q-border);
        /* grey deck background so the white slides of the renderer read as slides */
        background: #eef1f6;
        overflow-x: auto;
        overflow-y: auto;
        overscroll-behavior: contain;
    }

    /* ── Vuetify field overrides — match the DS radius/border ────── */
    .rb-panel >>> .v-input--dense .v-input__slot,
    .rb-menu-inner >>> .v-input--dense .v-input__slot {
        border-radius: 10px;
        min-height: 42px;
    }
    .rb-panel >>> .v-text-field--outlined fieldset {
        border-color: var(--q-border);
    }
    .rb-panel >>> .v-text-field--outlined.v-input--is-focused fieldset {
        border-color: var(--q-primary);
    }
    .rb-panel >>> .v-label {
        font-size: 0.81rem;
        color: var(--q-fg3);
    }
    .rb-panel >>> .v-select__selection,
    .rb-panel >>> input,
    .rb-panel >>> textarea {
        font-size: 0.81rem;
        color: var(--q-fg1);
    }
    .rb-panel >>> .v-input--selection-controls__input {
        margin-right: 4px;
    }

    @media (max-width: 1100px) {
        .report-builder {
            height: auto;
            min-height: 100vh;
            overflow: visible;
        }
        .rb-body {
            height: auto;
            flex-wrap: wrap;
            overflow-y: auto;
            overflow-x: hidden;
        }
        .rb-sidebar,
        .rb-preview-col {
            flex: 0 0 100%;
            max-width: 100%;
        }
        .rb-panel-scroll,
        .rb-seclist {
            overflow: visible;
        }
    }
</style>
